import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { Layout } from '../components/layout/Layout';
import { Card } from '../components/common/Card';
import { PlayerPairingMatrix } from '../components/stats/PlayerPairingMatrix';

interface PlayerStats {
  id: string;
  name: string;
  matches_played: number;
  wins: number;
  losses: number;
  draws: number;
  win_rate: number;
  goals_for: number;
  goals_against: number;
  goal_difference: number;
  goals: number;
  assists: number;
  contributions: number;
}

interface PairingData {
  players: Array<{ id: string; name: string }>;
  pairings: Array<{
    player1Id: string;
    player1Name: string;
    player2Id: string;
    player2Name: string;
    totalMatchesTogether: number;
    timesOnSameTeam: number;
    percentage: number;
  }>;
}

const Stats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<PlayerStats[]>([]);
  const [pairingData, setPairingData] = useState<PairingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'wins' | 'losses' | 'contributions'>('wins');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [statsResponse, pairingResponse] = await Promise.all([
        apiClient.getPlayerStats(),
        apiClient.getPlayerPairingMatrix(),
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }

      if (pairingResponse.success) {
        setPairingData({
          players: pairingResponse.players,
          pairings: pairingResponse.pairings,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load player stats');
    } finally {
      setLoading(false);
    }
  };

  const getSortedStats = (type: 'wins' | 'losses' | 'contributions') => {
    const sorted = [...stats].filter(s => s.matches_played > 0);

    switch (type) {
      case 'wins':
        return sorted.sort((a, b) => b.wins - a.wins).slice(0, 10);
      case 'losses':
        return sorted.sort((a, b) => b.losses - a.losses).slice(0, 10);
      case 'contributions':
        return sorted.sort((a, b) => b.contributions - a.contributions).slice(0, 10);
      default:
        return sorted;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
                Error Loading Stats
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  if (stats.length === 0 || stats.every(s => s.matches_played === 0)) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                No Stats Yet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Player statistics will appear here once you save matches with scores.
              </p>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  const topWins = getSortedStats('wins');
  const topLosses = getSortedStats('losses');
  const topContributions = getSortedStats('contributions');

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Player Statistics
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            View individual player performance across all matches
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Most Wins */}
          <Card>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <span className="text-2xl mr-2">🏆</span>
              Most Wins
            </h2>
            <div className="space-y-3">
              {topWins.map((player, index) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-transparent dark:from-green-900/20 dark:to-transparent rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-green-100 dark:bg-green-900/30 rounded-full font-bold text-green-700 dark:text-green-300">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {player.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {player.matches_played} matches • {player.win_rate.toFixed(0)}% win rate
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {player.wins}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">wins</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Most Losses */}
          <Card>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <span className="text-2xl mr-2">📉</span>
              Most Losses
            </h2>
            <div className="space-y-3">
              {topLosses.map((player, index) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-red-50 to-transparent dark:from-red-900/20 dark:to-transparent rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full font-bold text-red-700 dark:text-red-300">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {player.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {player.matches_played} matches • {player.win_rate.toFixed(0)}% win rate
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {player.losses}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">losses</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Goal Contributions */}
          <Card>
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
              <span className="text-2xl mr-2">⚽</span>
              Top Contributors
            </h2>
            <div className="space-y-3">
              {topContributions.map((player, index) => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20 dark:to-transparent rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 rounded-full font-bold text-blue-700 dark:text-blue-300">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {player.name}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {player.goals}G + {player.assists}A
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {player.contributions}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">G+A</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Full Stats Table */}
        <Card>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            📊 All Player Statistics
          </h2>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Player
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Matches
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    W/L/D
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Win %
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    GF/GA
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    +/-
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    G
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    A
                  </th>
                  <th className="px-3 md:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    G+A
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {stats.filter(s => s.matches_played > 0).map((player) => (
                  <tr key={player.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {player.name}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {player.matches_played}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      <span className="text-green-600 dark:text-green-400">{player.wins}</span>
                      /
                      <span className="text-red-600 dark:text-red-400">{player.losses}</span>
                      /
                      <span className="text-gray-500">{player.draws}</span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm">
                      <span className={
                        player.win_rate >= 60 ? 'text-green-600 dark:text-green-400 font-semibold' :
                        player.win_rate >= 40 ? 'text-gray-900 dark:text-gray-100' :
                        'text-red-600 dark:text-red-400 font-semibold'
                      }>
                        {player.win_rate.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {player.goals_for}/{player.goals_against}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm">
                      <span className={
                        player.goal_difference > 0 ? 'text-green-600 dark:text-green-400 font-semibold' :
                        player.goal_difference < 0 ? 'text-red-600 dark:text-red-400 font-semibold' :
                        'text-gray-500'
                      }>
                        {player.goal_difference > 0 ? '+' : ''}{player.goal_difference}
                      </span>
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {player.goals}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-gray-100">
                      {player.assists}
                    </td>
                    <td className="px-3 md:px-6 py-3 md:py-4 whitespace-nowrap text-center text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {player.contributions}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
            About Statistics
          </h3>
          <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <li>• Stats are calculated from all saved matches with scores</li>
            <li>• Goal contributions (G+A) are tracked via the Watch feature</li>
            <li>• Win rate shows the percentage of matches won by each player</li>
            <li>• Goal difference (+/-) shows the total goals for minus goals against</li>
          </ul>
        </Card>

        {/* Player Pairing Matrix */}
        {pairingData && pairingData.players.length > 1 && (
          <Card className="mt-8">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              🤝 Player Pairing Matrix
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Shows how often players have been on the same team in their last 10 matches together.
              Green indicates balanced (35-65%), orange indicates moderate imbalance (25-34% or 66-75%),
              and red indicates highly imbalanced (&lt;25% or &gt;75%).
            </p>
            <PlayerPairingMatrix
              players={pairingData.players}
              pairings={pairingData.pairings}
            />
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Stats;
